// =============================================================================
// client/src/pages/campaigns/CampaignPortal.jsx | v2.0 | SQ-Chrome S1 | P9b — W-4, W-5
// =============================================================================
// v2.0 (SQ-Chrome S1, 2026-08-27): CHROME ONLY. Board vocabulary from
// lib/flux/board.js — paper ground, 2px ink outlines, ledge shadow, dashed
// perforation dividers, spine cards, uppercase pill controls. NO DATA LOGIC
// TOUCHED: every fetch, useState, callback, handler and server-formatted string
// is byte-identical to v1.1. The existing "Back to Dashboard" control was
// restyled in place and re-labelled to match the board's other backlinks; it
// was already a navigate('/') and this page never dead-ended.
// =============================================================================
// The RC portal: the campaigns for my assigned ∪ allowlisted recruits, and my
// hot-lead alerts.
//
// 🔴 THE PORTAL SENDS NO FILTER OF ITS OWN. It asks for the campaign list and
// the server returns only campaigns whose recruit is in scope. An RC with no
// recruits gets {campaigns:[], total:0} — an empty list is the server's answer,
// not something this page computes. Scope is not a query parameter here; it is
// the server's identity of the caller.
//
// EVERY STATUS WORD, NEXT ACTION AND ETA IS P9a's DECODER (`describeCampaign`),
// arriving as status_display / next_action / eta on the same list route P9a's
// own page uses. THIS PAGE COMPUTES NO STATUS. A client that reads pause_reason
// to decide anything is the RecruitMail defect this phase exists to delete —
// RM decoded state in the browser, in two copies, and they drifted.
//
// ⚠ Alerts are fetched on MOUNT and on WINDOW FOCUS. There is deliberately no
// interval (§10, logged with a trigger): an always-open portal polling every
// N seconds is a standing load on the general limiter for a page whose data
// changes a few times a day.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useMe } from '../../lib/flux/useMe.js';
import { severityClass, bySortRankThenRecruit } from '../../lib/flux/status.js';
import Toast from '../../components/Toast.jsx';
import {
  PAGE, SHELL, PANEL, PANEL_QUIET, CARD_SPINE, DASH,
  BTN_GHOST, H1, H2, LABEL, BODY_MUTED,
} from '../../lib/flux/board.js';

export default function CampaignPortal() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useMe();

  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [alertTotal, setAlertTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const loadAlerts = useCallback(() => {
    api.get('/api/flux/alerts?limit=100')
      .then((body) => { setAlerts(body?.alerts || []); setAlertTotal(body?.total || 0); })
      .catch(() => { /* the campaign list is the page; alerts are an overlay */ });
  }, []);

  const loadCampaigns = useCallback(() => {
    setLoading(true);
    api.get('/api/flux/campaigns/admin/dashboard?limit=100')
      .then((body) => { setCampaigns(body?.campaigns || []); setTotal(body?.total || 0); })
      .catch((err) => setError(err.body?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCampaigns(); loadAlerts(); }, [loadCampaigns, loadAlerts]);

  useEffect(() => {
    const onFocus = () => loadAlerts();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadAlerts]);

  // Grouped by campaign so Dismiss All has something to be "all" OF.
  const byCampaign = alerts.reduce((acc, a) => {
    (acc[a.campaign_id] ||= { campaign_id: a.campaign_id, campaign_name: a.campaign_name, recruit_name: a.recruit_name, rows: [] })
      .rows.push(a);
    return acc;
  }, {});
  const groups = Object.values(byCampaign);

  async function dismissOne(alert) {
    const snapshot = alerts;
    // Optimistic — but REVERTED with a toast on error. RM cleared the banner
    // regardless of what the server said and never put a row back.
    setAlerts((list) => list.filter((a) => a.id !== alert.id));
    try {
      await api.post('/api/flux/alerts/dismiss', {
        campaign_id: alert.campaign_id, coach_email: alert.coach_email,
      });
      setAlertTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      setAlerts(snapshot);
      setToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  async function dismissAll(group) {
    const snapshot = alerts;
    setAlerts((list) => list.filter((a) => a.campaign_id !== group.campaign_id));
    try {
      const body = await api.post('/api/flux/alerts/dismiss-all', { campaign_id: group.campaign_id });
      setAlertTotal((t) => Math.max(0, t - (body?.dismissed || 0)));
      setToast({ message: `Dismissed ${body?.dismissed ?? 0} alert(s).`, tone: 'success' });
    } catch (err) {
      setAlerts(snapshot);
      setToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  if (meLoading || loading) {
    return (
      <div className={PAGE}>
        <div className={`${BODY_MUTED} py-24 text-center`}>Loading…</div>
      </div>
    );
  }

  // An RC sees banners; an ALLOWLISTED VIEWER sees cards and no alerts, because
  // hot-lead rows target the ASSIGNED RC. Saying so beats an empty banner area
  // that reads as a bug.
  const viewerWithoutAlerts = campaigns.length > 0 && alerts.length === 0;

  return (
    <div className={PAGE}>
      <div className={`${SHELL} py-10`}>

        {/* Masthead. One line under the title, matching the board's other
            pages — not a green sub then a grey sub. */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div className="min-w-0">
            <h1 className={H1}>My Recruits</h1>
            <p className={`${BODY_MUTED} mt-2`}>
              {me?.team?.full_name ? `${me.team.full_name} · ` : ''}{total} campaign{total === 1 ? '' : 's'}
            </p>
          </div>
          <button type="button" className={`${BTN_GHOST} shrink-0 pt-3`} onClick={() => navigate('/')}>
            ← Front Office
          </button>
        </div>

        {error && (
          <div className="border-2 border-red-700 bg-red-50 text-red-800 rounded-2xl px-4 py-3 text-sm mb-6">{error}</div>
        )}

        {groups.length > 0 && (
          <section className={`${PANEL} mb-8`}>
            <div className="px-6 py-5">
              <h2 className={H2}>Hot leads ({alertTotal})</h2>
            </div>
            {groups.map((g) => (
              <div key={g.campaign_id} className={`${DASH} px-6 py-5`}>
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="font-semibold text-[#0E2A20] text-sm min-w-0 truncate">
                    {g.recruit_name ? `${g.recruit_name} — ` : ''}{g.campaign_name}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 border-dashed border-[#0E2A20] text-[#0E2A20] hover:bg-white"
                    onClick={() => dismissAll(g)}
                  >
                    Dismiss all ({g.rows.length})
                  </button>
                </div>
                <ul className="space-y-2">
                  {g.rows.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 text-sm text-[#0E2A20]">
                      <span className="min-w-0 truncate">
                        {a.coach_name || a.coach_email}
                        {a.school ? ` · ${a.school}` : ''}
                        {a.score != null ? ` · score ${a.score}` : ''}
                      </span>
                      <button
                        type="button"
                        className="shrink-0 w-6 h-6 leading-none rounded-full border-2 border-[#0E2A20] text-[#0E2A20] hover:bg-[#0E2A20] hover:text-white transition-colors"
                        aria-label="Dismiss"
                        onClick={() => dismissOne(a)}
                      >×</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {viewerWithoutAlerts && (
          <p className={`${BODY_MUTED} text-xs mb-8`}>
            Hot-lead alerts go to the recruit&apos;s assigned coordinator. You can see these campaigns
            because you were granted access to the recruit.
          </p>
        )}

        {campaigns.length === 0 ? (
          <div className={PANEL_QUIET}>
            <div className={`${BODY_MUTED} py-16 text-center`}>
              No campaigns for your recruits yet.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Server order, re-sorted IN PAGE by the server's own sort_rank.
                The rank is the server's; this only applies it. */}
            {[...campaigns].sort(bySortRankThenRecruit).map((c) => (
              <CampaignCard
                key={c.id} campaign={c}
                hotLeads={(byCampaign[c.id]?.rows || []).length}
                onOpen={() => navigate(`/campaigns/${c.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}

function CampaignCard({ campaign, hotLeads, onOpen }) {
  const d = campaign.status_display || {};
  const next = campaign.next_action || {};
  const s = campaign.stats || {};

  // eta arrives as {days} or {hours} or null — the server decided which.
  const eta = campaign.eta
    ? (campaign.eta.days != null
      ? `~${campaign.eta.days} day${campaign.eta.days === 1 ? '' : 's'} remaining`
      : `~${campaign.eta.hours} hour${campaign.eta.hours === 1 ? '' : 's'} remaining`)
    : null;

  return (
    <button
      type="button" onClick={onOpen}
      className={`${CARD_SPINE} w-full text-left p-5 transition-transform active:translate-y-[2px]`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-[0.2em] ${severityClass(d.severity)}`}>
              {d.badge || '—'}
            </span>
            {hotLeads > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-[0.2em] bg-amber-100 text-amber-800">
                {hotLeads} hot
              </span>
            )}
          </div>
          <div className="font-display font-semibold text-[#0E2A20] text-lg leading-tight truncate">
            {campaign.recruitName} | {campaign.name}
          </div>
          <div className={`${BODY_MUTED} text-xs truncate mt-0.5`}>{campaign.send_from_email}</div>
        </div>

        {/* open_rate / click_rate arrive PRE-FORMATTED from the server. Rendered
            as given — this page formats no number. */}
        <div className={`${BODY_MUTED} text-right text-xs whitespace-nowrap shrink-0`}>
          <div>{s.sent ?? 0} sent · {s.opened ?? 0} opens ({s.open_rate ?? '0%'})</div>
          <div>{s.clicked ?? 0} clicks ({s.click_rate ?? '0%'}) · {s.bounced ?? 0} bounced</div>
        </div>
      </div>

      <div className={`${DASH} mt-4 pt-3 text-sm`}>
        <span className="text-[#0E2A20]">{d.label}</span>
        {next.text && <span className="text-[#5A5A5A]"> — {next.text}</span>}
        {eta && <div className={`${LABEL} mt-1.5`}>{eta}</div>}
      </div>
    </button>
  );
}
